import {
  fetchTicketPreviewImageSrc,
  getTicketImageResourceId,
  getTicketRowImages,
  resolveTicketImageSrc,
} from "helpers/ticketResourceImage";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Lightbox from "react-image-lightbox";


const TicketImagesModal = props => {
  const { isOpen, toggle, ticket } = props;
  const images = useMemo(() => getTicketRowImages(ticket), [ticket]);

  const [isGallery, setIsGallery] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [slides, setSlides] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const blobUrlsRef = useRef([]);

  const revokeBlobUrls = useCallback(() => {
    blobUrlsRef.current.forEach(u => {
      try {
        URL.revokeObjectURL(u);
      } catch {
        /* ignore */
      }
    });
    blobUrlsRef.current = [];
  }, []);

  useEffect(() => {
    setSlides(
      images.map(img => {
        if (typeof img === "string") {
          return {
            id: null,
            caption: "",
            url: resolveTicketImageSrc(img),
          };
        }
        return {
          id: getTicketImageResourceId(img),
          caption: img.file_name ?? "",
          url: resolveTicketImageSrc(img),
        };
      })
    );
    setPhotoIndex(0);
    revokeBlobUrls();
  }, [images, ticket?.id, revokeBlobUrls]);

  useEffect(() => {
    if (!isOpen) {
      setIsGallery(false);
      revokeBlobUrls();
    }
  }, [isOpen, revokeBlobUrls]);

  useLayoutEffect(() => {
    if (isOpen && images.length > 0) {
      setPhotoIndex(0);
      setIsGallery(true);
    }
  }, [isOpen, ticket?.id, images.length]);

  useEffect(() => {
    if (isOpen && ticket && images.length === 0) {
      toggle();
    }
  }, [isOpen, ticket, images.length, toggle]);

  useEffect(() => {
    if (!isGallery) {
      revokeBlobUrls();
      setGalleryLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setGalleryLoading(true);
      const newBlobs = [];
      try {
        const nextSlides = await Promise.all(
          images.map(async raw => {
            const caption = typeof raw === "object" ? raw.file_name ?? "" : "";
            if (typeof raw === "string") {
              return {
                id: null,
                caption,
                url: resolveTicketImageSrc(raw),
              };
            }
            const rid = getTicketImageResourceId(raw);
            if (rid == null) {
              return {
                id: null,
                caption,
                url: resolveTicketImageSrc(raw),
              };
            }
            try {
              const url = await fetchTicketPreviewImageSrc(rid, raw);
              if (url && url.startsWith("blob:")) {
                newBlobs.push(url);
              }
              return { id: rid, caption, url: url || "" };
            } catch {
              return {
                id: rid,
                caption,
                url: resolveTicketImageSrc(raw),
              };
            }
          })
        );
        if (cancelled) {
          newBlobs.forEach(u => URL.revokeObjectURL(u));
          return;
        }
        blobUrlsRef.current = newBlobs;
        setSlides(nextSlides);
      } finally {
        if (!cancelled) {
          setGalleryLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isGallery, images, revokeBlobUrls]);

  const handleCloseLightbox = useCallback(() => {
    setIsGallery(false);
    toggle();
  }, [toggle]);

  const currentUrl = slides[photoIndex]?.url;
  const showLightbox =
    isOpen && isGallery && !galleryLoading && Boolean(currentUrl);

  const slideCaptionText = slides[photoIndex]?.caption?.trim();
  const shortText = ticket?.short_text?.trim();

  const imageCaption = useMemo(() => {
    const parts = [];
    if (shortText) {
      parts.push(shortText);
    }
    if (slides.length > 1) {
      parts.push(`${photoIndex + 1} / ${slides.length}`);
    }
    if (slideCaptionText) {
      parts.push(slideCaptionText);
    }
    return parts.length ? parts.join(" · ") : undefined;
  }, [shortText, slides.length, photoIndex, slideCaptionText]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {isGallery && galleryLoading ? (
        <div
          className="ticket-images-loading-overlay"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2055,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: "1rem",
          }}
        >
          Đang tải ảnh…
        </div>
      ) : null}

      {showLightbox ? (
        <Lightbox
          reactModalStyle={{
            overlay: { zIndex: 2060 },
          }}
          mainSrc={currentUrl}
          nextSrc={slides[(photoIndex + 1) % slides.length]?.url}
          prevSrc={
            slides[(photoIndex + slides.length - 1) % slides.length]?.url
          }
          enableZoom
          onCloseRequest={handleCloseLightbox}
          onMovePrevRequest={() =>
            setPhotoIndex((photoIndex + slides.length - 1) % slides.length)
          }
          onMoveNextRequest={() =>
            setPhotoIndex((photoIndex + 1) % slides.length)
          }
          imageCaption={imageCaption}
        />
      ) : null}
    </>
  );
};

export default TicketImagesModal;
